import { Box, Chip, Divider, LinearProgress, Stack, Typography } from '@mui/material';

import type { AchievementProgress, ChildAchievements } from '../api/types';

import { FAMILY_LABEL } from './achievementFamilies';
import { days } from './slovakPlural';

type AchievementPanelProps = {
  data: ChildAchievements;
};

/**
 * One child's achievements, as support needs to read them.
 *
 * Both streak numbers are shown, and the best one is the larger of the two on the page. A child's current
 * streak falls to zero after an illness by design, and an operator looking at a zero needs the number next
 * to it that did not fall — otherwise the first support answer is "she lost everything", which is exactly
 * the reading the engine was built to prevent.
 */
export const AchievementPanel = ({ data }: AchievementPanelProps) => {
  const earned = data.achievements.filter((one) => one.isEarned);

  return (
    <>
      <Stack direction="row" spacing={3} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        <Figure label="Získané" value={`${data.earnedCount} / ${data.totalCount}`} />
        <Figure label="Najdlhšia séria" value={days(data.bestStreakDays)} emphasis />
        <Figure label="Aktuálna séria" value={days(data.currentStreakDays)} muted />
      </Stack>

      {earned.length === 0 ? (
        <Typography variant="body2" color="text.disabled">
          Zatiaľ nič nezískala ani nezískal. Prvé ocenenie prichádza po prvom dokončenom tréningu.
        </Typography>
      ) : (
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
          {earned.map((one) => (
            <Chip
              key={one.code}
              size="small"
              color="success"
              variant="outlined"
              label={one.name}
              title={`${FAMILY_LABEL[one.family] ?? one.family} · ${one.description}${
                one.earnedAt ? ` · ${new Date(one.earnedAt).toLocaleDateString('sk-SK')}` : ''
              }`}
            />
          ))}
        </Stack>
      )}

      {data.nextUp.length > 0 ? (
        <>
          <Divider sx={{ my: 2.5 }} />
          <Typography variant="overline" color="text.secondary">
            Najbližšie méty
          </Typography>
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            {data.nextUp.map((one: AchievementProgress) => (
              <Box key={one.code}>
                <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 0.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 560 }}>
                    {one.name}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontVariantNumeric: 'tabular-nums' }}
                  >
                    {one.current} / {one.threshold}
                  </Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(100, (one.current / one.threshold) * 100)}
                  sx={{ height: 6 }}
                />
              </Box>
            ))}
          </Stack>
        </>
      ) : null}
    </>
  );
};

type FigureProps = {
  label: string;
  value: string;
  emphasis?: boolean;
  muted?: boolean;
};

const Figure = ({ label, value, emphasis, muted }: FigureProps) => (
  <Box>
    <Typography variant="overline" color="text.secondary" sx={{ display: 'block' }}>
      {label}
    </Typography>
    <Typography
      variant={emphasis ? 'h5' : 'h6'}
      color={muted ? 'text.secondary' : 'text.primary'}
      sx={{ fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.02em' }}
    >
      {value}
    </Typography>
  </Box>
);
